// SPDX-License-Identifier: AGPL-3.0-or-later

import {loadRoomSound, type RoomSoundSource} from '@app/features/voice_scene/audio/RoomAmbience';
import type {Vec3} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import type {VoiceRoomSfx} from '@fluxer/constants/src/VoiceRoomSounds';

// Room event sounds sit under the voices.
export const SFX_BUS_GAIN = 10 ** (-8 / 20);
// A crowd reacting at once plays this many shots and drops the rest.
export const SFX_MAX_PLAYING = 8;
const PER_SENDER_INTERVAL_MS = 1000;
// Footsteps have their own budget: a run is 3 a second, anything much faster is a flood.
export const STEP_MIN_INTERVAL_MS = 150;
// A sound still downloading this long after its event no longer belongs to it.
const STALE_MS = 500;

export interface RoomSfxShot {
	// Seconds after now on the context clock (a footstep due before the next walking tick).
	delayS?: number;
	// 0 to 1 on top of the sound's own gain; footsteps go up to 2 (VoiceSceneSettings.footstepVolume).
	level?: number;
}

export interface RoomSfx {
	// at: listener-relative (listener at the origin facing -Z), as the room's speakers are placed.
	play(name: VoiceRoomSfx, at: Vec3, sender: string, shot?: RoomSfxShot): void;
	// Downloads these now, so their first event does not go stale waiting for the file.
	warm(names: ReadonlyArray<VoiceRoomSfx>): void;
	setDeafened(deafened: boolean): void;
	dispose(): void;
}

interface Shot {
	source: AudioBufferSourceNode;
	gain: GainNode;
	panner: PannerNode;
}

// One decoded buffer per sound, fetched on its first event and kept for the room's life (the set is the manifest's,
// a dozen short clips); every shot is a fresh source → gain → equal-power panner at the event → the room's sfx bus.
// busGain: the room's scene sounds (useRoomAmbience) have a player of their own at the ambience's level.
export function createRoomSfx({
	context,
	soundOf,
	now = () => performance.now(),
	busGain = SFX_BUS_GAIN,
}: {
	context: AudioContext;
	soundOf: (name: VoiceRoomSfx) => RoomSoundSource;
	now?: () => number;
	busGain?: number;
}): RoomSfx {
	const abort = new AbortController();
	const bus = context.createGain();
	bus.gain.value = busGain;
	bus.connect(context.destination);
	const buffers = new Map<VoiceRoomSfx, Promise<AudioBuffer | null>>();
	const lastBySender = new Map<string, number>();
	const playing = new Set<Shot>();
	let deafened = false;

	const bufferOf = (name: VoiceRoomSfx): Promise<AudioBuffer | null> => {
		let buffer = buffers.get(name);
		if (!buffer) {
			buffer = loadRoomSound(context, soundOf(name).url, abort.signal);
			buffers.set(name, buffer);
		}
		return buffer;
	};
	const release = (shot: Shot): void => {
		shot.source.onended = null;
		shot.source.disconnect();
		shot.gain.disconnect();
		shot.panner.disconnect();
		playing.delete(shot);
	};
	const fire = (
		buffer: AudioBuffer | null,
		name: VoiceRoomSfx,
		at: Vec3,
		askedAt: number,
		{delayS = 0, level = 1}: RoomSfxShot,
	): void => {
		if (!buffer || abort.signal.aborted || deafened || playing.size >= SFX_MAX_PLAYING) return;
		if (now() - askedAt > STALE_MS) return;
		const source = context.createBufferSource();
		source.buffer = buffer;
		const gain = context.createGain();
		gain.gain.value = soundOf(name).gain * level;
		const panner = context.createPanner();
		panner.panningModel = 'equalpower';
		panner.distanceModel = 'linear';
		panner.rolloffFactor = 0;
		// Mono in, like the voices: equal power on one channel never adds gain.
		panner.channelCount = 1;
		panner.channelCountMode = 'explicit';
		panner.positionX.value = at.x;
		panner.positionY.value = at.y;
		panner.positionZ.value = at.z;
		source.connect(gain);
		gain.connect(panner);
		panner.connect(bus);
		const shot = {source, gain, panner};
		playing.add(shot);
		source.onended = () => release(shot);
		source.start(delayS > 0 ? context.currentTime + delayS : undefined);
	};

	return {
		play(name, at, sender, shot = {}) {
			if (abort.signal.aborted || deafened) return;
			const askedAt = now();
			const step = name.startsWith('step_');
			const key = step ? `${sender}:step` : sender;
			const last = lastBySender.get(key);
			if (last !== undefined && askedAt - last < (step ? STEP_MIN_INTERVAL_MS : PER_SENDER_INTERVAL_MS)) return;
			lastBySender.set(key, askedAt);
			void bufferOf(name).then((decoded) => fire(decoded, name, at, askedAt, shot));
		},
		warm(names) {
			if (abort.signal.aborted || deafened) return;
			for (const name of names) void bufferOf(name);
		},
		setDeafened(next) {
			deafened = next;
			bus.gain.setTargetAtTime(next ? 0 : busGain, context.currentTime, 0.02);
		},
		dispose() {
			abort.abort();
			for (const shot of [...playing]) {
				shot.source.stop();
				shot.source.buffer = null;
				release(shot);
			}
			buffers.clear();
			lastBySender.clear();
			bus.disconnect();
		},
	};
}
